import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, StyleSheet } from 'react-native';
import { Header } from '../../components/Header';
import { StatCard } from '../../components/StatCard';
import { useGetDashboardAnalyticsQuery, useGetPendingMeetingsQuery } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

export const AdminDashboardScreen = ({ navigation }: any) => {
  const { data: analytics, isLoading, refetch } = useGetDashboardAnalyticsQuery();
  const { data: pendingMeetings = [] } = useGetPendingMeetingsQuery();
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="Master Admin Console" subtitle="System Analytics & Decision Control Center" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 24 }}
        refreshControl={<RefreshControl refreshing={isLoading} onRefresh={refetch} />}
      >
        {/* Pending Approvals Alert Banner */}
        {pendingMeetings.length > 0 && (
          <TouchableOpacity
            onPress={() => navigation.navigate('PendingApprovals')}
            style={styles.alertBanner}
          >
            <View style={styles.alertLeft}>
              <Text style={{ fontSize: 24, marginRight: 12 }}>⚠️</Text>
              <View style={{ flex: 1 }}>
                <Text style={styles.alertTitle}>
                  {pendingMeetings.length} Pending Meeting Approvals
                </Text>
                <Text style={styles.alertSubtitle}>
                  Review visitor requests and authorize slot bookings
                </Text>
              </View>
            </View>
            <View style={styles.alertBtn}>
              <Text style={styles.alertBtnText}>Review →</Text>
            </View>
          </TouchableOpacity>
        )}

        {/* Analytics Grid */}
        <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>
          Overall Metrics
        </Text>
        <View style={styles.statsGrid}>
          <StatCard
            title="Today Meetings"
            value={analytics?.todayTotalMeetings ?? 0}
            icon="📅"
            accentColor={COLORS.primary}
          />
          <StatCard
            title="Pending Requests"
            value={analytics?.pendingApprovals ?? 0}
            icon="⏳"
            accentColor={COLORS.warning}
          />
        </View>
        <View style={styles.statsGrid}>
          <StatCard
            title="Confirmed Meetings"
            value={analytics?.confirmedMeetings ?? 0}
            icon="✅"
            accentColor={COLORS.success}
          />
          <StatCard
            title="Rejected Meetings"
            value={analytics?.rejectedMeetings ?? 0}
            icon="❌"
            accentColor={COLORS.danger}
          />
        </View>

        {/* Schedule Utilization Card */}
        <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, marginVertical: 12 }]}>
          <Text style={[styles.cardTitle, { color: theme.textPrimary }]}>
            📊 Schedule Slot Utilization Rate
          </Text>
          <Text style={[styles.cardSub, { color: theme.textSecondary }]}>
            Percentage of Master Admin work slots booked across all active schedules
          </Text>

          <View style={styles.progressTrack}>
            <View
              style={[
                styles.progressBar,
                { width: `${analytics?.scheduleUtilizationPercent || 0}%` },
              ]}
            />
          </View>

          <View style={styles.progressFooter}>
            <Text style={[styles.progressLabel, { color: theme.textSecondary }]}>Current Occupancy</Text>
            <Text style={styles.progressVal}>
              {analytics?.scheduleUtilizationPercent || 0}%
            </Text>
          </View>
        </View>

        {/* Quick Launch Actions */}
        <Text style={[styles.sectionTitle, { color: theme.textPrimary, marginTop: 12 }]}>
          Quick Actions
        </Text>
        <View style={styles.quickGrid}>
          <TouchableOpacity
            onPress={() => navigation.navigate('ManageSchedules')}
            style={[styles.tileBtn, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
          >
            <Text style={{ fontSize: 24, marginRight: 10 }}>🗓️</Text>
            <View>
              <Text style={[styles.tileTitle, { color: theme.textPrimary }]}>Work Schedules</Text>
              <Text style={[styles.tileSub, { color: theme.textSecondary }]}>Set daily slots</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => navigation.navigate('UserManagement')}
            style={[styles.tileBtn, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
          >
            <Text style={{ fontSize: 24, marginRight: 10 }}>👥</Text>
            <View>
              <Text style={[styles.tileTitle, { color: theme.textPrimary }]}>User Roles</Text>
              <Text style={[styles.tileSub, { color: theme.textSecondary }]}>Manage Staff</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => navigation.navigate('NotificationLogs')}
            style={[styles.tileBtn, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
          >
            <Text style={{ fontSize: 24, marginRight: 10 }}>📲</Text>
            <View>
              <Text style={[styles.tileTitle, { color: theme.textPrimary }]}>Audit Logs</Text>
              <Text style={[styles.tileSub, { color: theme.textSecondary }]}>WhatsApp & Email</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => navigation.navigate('ApprovedMeetings')}
            style={[styles.tileBtn, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
          >
            <Text style={{ fontSize: 24, marginRight: 10 }}>✅</Text>
            <View>
              <Text style={[styles.tileTitle, { color: theme.textPrimary }]}>Approved List</Text>
              <Text style={[styles.tileSub, { color: theme.textSecondary }]}>Confirmed visitors</Text>
            </View>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  alertBanner: {
    backgroundColor: COLORS.warningBg,
    borderColor: COLORS.warning,
    borderWidth: 1,
    padding: 16,
    borderRadius: 20,
    marginBottom: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  alertLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 10,
  },
  alertTitle: {
    color: COLORS.warning,
    fontSize: 14,
    fontWeight: '800',
  },
  alertSubtitle: {
    color: COLORS.warning,
    fontSize: 11,
    opacity: 0.9,
    marginTop: 2,
  },
  alertBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: COLORS.warning,
    borderRadius: 12,
  },
  alertBtnText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '800',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 10,
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 10,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: '800',
    marginBottom: 2,
  },
  cardSub: {
    fontSize: 11,
    marginBottom: 16,
  },
  progressTrack: {
    height: 14,
    borderRadius: 7,
    backgroundColor: 'rgba(148, 163, 184, 0.2)',
    overflow: 'hidden',
    marginBottom: 10,
  },
  progressBar: {
    height: '100%',
    borderRadius: 7,
    backgroundColor: COLORS.primary,
  },
  progressFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressLabel: {
    fontSize: 12,
  },
  progressVal: {
    fontSize: 18,
    fontWeight: '800',
    color: COLORS.primary,
  },
  quickGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  tileBtn: {
    width: '48%',
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  tileTitle: {
    fontSize: 13,
    fontWeight: '800',
  },
  tileSub: {
    fontSize: 10,
    marginTop: 2,
  },
});
